import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  IconButton,
  Button,
  InputAdornment
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

const IdeaChatDialog = ({ open, onClose, ideaId, userId, users }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    if (open && ideaId) {
      fetchChatMessages();
      setNewMessage('');
    }
  }, [open, ideaId]);

  const fetchChatMessages = async () => {
    try {
      const response = await fetch(
        `https://api.hub4food.colab4food.com/api/ideas/${ideaId}/chat`
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
    }
  };

  const sendNotification = async () => {
    try {
      const response = await fetch(
        'https://api.hub4food.colab4food.com/api/send-idea-comment-notification',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ideaId,
            commentUserId: userId
          })
        }
      );

      if (!response.ok) {
        console.error('Failed to send notifications');
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageToSend = newMessage;

    try {
      // 1. Envia a mensagem
      const response = await fetch(
        `https://api.hub4food.colab4food.com/api/ideas/${ideaId}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            message: messageToSend
          })
        }
      );

      if (response.ok) {
        // 2. Limpa o campo de mensagem
        setNewMessage('');

        // 3. Atualiza a lista de mensagens
        await fetchChatMessages();

        // 4. Envia notificação por email
        if (!messageToSend.startsWith('/system')) {
          await sendNotification();
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const getUserInfo = (messageUserId) => {
    return users.find(u => u.id === messageUserId) || {
      name: 'Unknown',
      email: 'unknown@example.com'
    };
  };

  const handleClose = () => {
    setNewMessage('');
    onClose();
  };

  // Participantes que já aparecem no chat
  const participants = [
    ...new Set(
      messages
        .filter(message => !message.is_system_message)
        .map(message => {
          const userInfo = getUserInfo(message.user_id);
          return userInfo.email || userInfo.name;
        })
    )
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        Idea Chat

        <Typography
          variant="subtitle2"
          color="text.secondary"
        >
          Participants: {
            participants.length > 0
              ? participants.join(', ')
              : 'No participants'
          }
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            height: '400px',
            overflowY: 'auto',
            mb: 2,
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1
          }}
        >
          {messages.length > 0 ? (
            messages.map((message, index) => {
              const isCurrentUser = message.user_id === userId;
              const userInfo = getUserInfo(message.user_id);

              return (
                <Box
                  key={message.id || index}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    flexDirection: isCurrentUser
                      ? 'row-reverse'
                      : 'row'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isCurrentUser
                        ? '#e3f2fd'
                        : '#ffffff',
                      boxShadow: 1,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {message.is_system_message
                        ? 'System'
                        : userInfo.name
                      }
                      {' - '}
                      {message.created_at
                        ? new Date(message.created_at).toLocaleString()
                        : ''
                      }
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: message.is_system_message
                          ? 'italic'
                          : 'normal',
                        color: message.is_system_message
                          ? 'error.main'
                          : 'text.primary'
                      }}
                    >
                      {message.message}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography>
              No messages yet
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          label="Type your message"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          margin="normal"
          multiline
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  color="primary"
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdeaChatDialog;